import { gql } from 'graphql-request';

const INVOICE_FIELDS = `
  invoiceId
  invoiceNumber
  status
  recipientEmail
  subtotal
  taxRate
  tax
  discountRate
  discountFixedAmount
  discountAmount
  total
  currency
  paymentTerms
  orderDescription
  poNumber
  nmiInvoiceId
  nmiTransactionId
  sentAt
  paidAt
  voidedAt
  lastError
  projectId
  clientId
  organizationId
  createdById
  version
  createdAt
  updatedAt
  lineItems {
    invoiceLineItemId
    description
    quantity
    unitOfMeasure
    unitPrice
    lineTotal
    taxAmount
    discountAmount
    displayOrder
    sourceType
    sourceEntityId
    taskId
    taskName
  }
  taskAssociations {
    invoiceTaskAssociationId
    taskId
  }
`;

export const getInvoiceQuery = gql`
  query invoice($invoiceId: ID!) {
    invoice(invoiceId: $invoiceId) {
      ${INVOICE_FIELDS}
    }
  }
`;

export const getInvoicesByProjectQuery = gql`
  query invoicesByProject($projectId: ID!, $statuses: [InvoiceStatus!]) {
    invoicesByProject(projectId: $projectId, statuses: $statuses) {
      ${INVOICE_FIELDS}
    }
  }
`;

export const getPaginatedInvoicesQuery = gql`
  query paginatedInvoices($input: GetPaginatedInvoicesInput!) {
    paginatedInvoices(input: $input) {
      count
      invoices {
        ${INVOICE_FIELDS}
      }
    }
  }
`;

export const taskInvoicePendingSummaryQuery = gql`
  query taskInvoicePendingSummary($taskId: ID!) {
    taskInvoicePendingSummary(taskId: $taskId) {
      materialCount
      equipmentCount
      sampleNames
    }
  }
`;

export const projectInvoicePendingSummaryQuery = gql`
  query projectInvoicePendingSummary($projectId: ID!) {
    projectInvoicePendingSummary(projectId: $projectId) {
      taskCount
      materialCount
      equipmentCount
      sampleNames
    }
  }
`;

export const getAvailableBudgetItemsForInvoiceQuery = gql`
  query availableBudgetItemsForInvoice($projectId: ID!) {
    availableBudgetItemsForInvoice(projectId: $projectId) {
      materials {
        sourceType
        sourceEntityId
        description
        quantity
        unitOfMeasure
        unitPrice
        total
        chargeToClient
        isAvailableForInvoice
        chargedToInvoiceId
        taskId
        taskName
        discountType
        discountValue
        discountAmount
      }
      equipment {
        sourceType
        sourceEntityId
        description
        quantity
        unitOfMeasure
        unitPrice
        total
        chargeToClient
        isAvailableForInvoice
        chargedToInvoiceId
        taskId
        taskName
        discountType
        discountValue
        discountAmount
      }
      expenses {
        sourceType
        sourceEntityId
        description
        quantity
        unitOfMeasure
        unitPrice
        total
        chargeToClient
        isAvailableForInvoice
        chargedToInvoiceId
        taskId
        taskName
        discountType
        discountValue
        discountAmount
      }
      delegatedTaskCosts {
        sourceType
        sourceEntityId
        description
        quantity
        unitOfMeasure
        unitPrice
        total
        chargeToClient
        isAvailableForInvoice
        chargedToInvoiceId
        taskId
        taskName
        discountType
        discountValue
        discountAmount
      }
    }
  }
`;

export const createInvoiceDraftMutation = gql`
  mutation createInvoiceDraft($input: CreateInvoiceDraftInput!) {
    createInvoiceDraft(input: $input) {
      ${INVOICE_FIELDS}
    }
  }
`;

export const updateInvoiceDraftMutation = gql`
  mutation updateInvoiceDraft($input: UpdateInvoiceDraftInput!) {
    updateInvoiceDraft(input: $input) {
      ${INVOICE_FIELDS}
    }
  }
`;

export const deleteInvoiceDraftMutation = gql`
  mutation deleteInvoiceDraft($invoiceId: ID!) {
    deleteInvoiceDraft(invoiceId: $invoiceId)
  }
`;

export const sendInvoiceMutation = gql`
  mutation sendInvoice($input: SendInvoiceInput!) {
    sendInvoice(input: $input) {
      ${INVOICE_FIELDS}
    }
  }
`;

export const voidInvoiceMutation = gql`
  mutation voidInvoice($input: VoidInvoiceInput!) {
    voidInvoice(input: $input) {
      ${INVOICE_FIELDS}
    }
  }
`;

export const resendInvoiceEmailMutation = gql`
  mutation resendInvoiceEmail($input: ResendInvoiceEmailInput!) {
    resendInvoiceEmail(input: $input) {
      ${INVOICE_FIELDS}
    }
  }
`;

export const refreshInvoiceStatusMutation = gql`
  mutation refreshInvoiceStatus($invoiceId: ID!) {
    refreshInvoiceStatus(invoiceId: $invoiceId) {
      ${INVOICE_FIELDS}
    }
  }
`;

const InvoiceGraphQL = {
  queries: {
    getInvoice: getInvoiceQuery,
    getInvoicesByProject: getInvoicesByProjectQuery,
    getPaginatedInvoices: getPaginatedInvoicesQuery,
    getAvailableBudgetItemsForInvoice: getAvailableBudgetItemsForInvoiceQuery,
    taskInvoicePendingSummary: taskInvoicePendingSummaryQuery,
    projectInvoicePendingSummary: projectInvoicePendingSummaryQuery,
  },
  mutations: {
    createInvoiceDraft: createInvoiceDraftMutation,
    updateInvoiceDraft: updateInvoiceDraftMutation,
    deleteInvoiceDraft: deleteInvoiceDraftMutation,
    sendInvoice: sendInvoiceMutation,
    voidInvoice: voidInvoiceMutation,
    resendInvoiceEmail: resendInvoiceEmailMutation,
    refreshInvoiceStatus: refreshInvoiceStatusMutation,
  },
};

export default InvoiceGraphQL;
